"use client";

import * as RadixTabs from "@radix-ui/react-tabs";
import Link from "next/link";
import type { ReactNode } from "react";

export interface TabItem {
  value: string;
  label: string;
  content: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onValueChange: (value: string) => void;
  ariaLabel: string;
}

/**
 * Dimensions of the same entity sharing one route (e.g. tab=acessos query param).
 * For dimensions that are distinct routes, use TabLinks instead — do not mix the
 * two ARIA models on the same tab row.
 */
export function Tabs({ items, value, onValueChange, ariaLabel }: TabsProps) {
  return (
    <RadixTabs.Root value={value} onValueChange={onValueChange}>
      <RadixTabs.List className="ui-tabs-list" aria-label={ariaLabel}>
        {items.map((item) => (
          <RadixTabs.Trigger key={item.value} value={item.value} className="ui-tab-trigger">
            {item.label}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {items.map((item) => (
        <RadixTabs.Content key={item.value} value={item.value} className="ui-tab-panel">
          {item.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}

export interface TabLink {
  href: string;
  label: string;
  active: boolean;
}

/** Dimensions with distinct routes: real <a> links + aria-current, not a tablist. */
export function TabLinks({ items, ariaLabel }: { items: TabLink[]; ariaLabel: string }) {
  return (
    <nav aria-label={ariaLabel} className="ui-tabs-list">
      {items.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          className="ui-tab-trigger"
          aria-current={item.active ? "page" : undefined}
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
}
